// Per-silo Operations tab — surfaces this silo's usage/cost telemetry and the
// Output Recirculation Buffer so the creator can quickly see how cheap their last
// outputs were AND recall any of the last 50 /ask or /run outputs straight into
// the Workbench staging area without losing their current Workbench state.
import { useEffect, useState } from "react";
import { ChartLine, Lightbulb } from "@phosphor-icons/react";
import UsageOverview from "@/components/UsageOverview";
import SiloVoucherPanel from "@/components/SiloVoucherPanel";
import SiloLLMHeaderCard from "@/components/SiloLLMHeaderCard";
import SiloSurfaceTunePanel from "@/components/SiloSurfaceTunePanel";
import { appendToStaging, readStaging } from "@/lib/stagingBuffer";

export default function OperationsTab({ siloId, siloName }) {
  const [stagingLen, setStagingLen] = useState(() => readStaging(siloId).length);
  // Jun 18, 2026 — bumped after a quick model switch so the per-surface tune
  // panel below refetches and its OVERRIDE badge stays in sync (it used to go
  // stale, showing "platform default" even after a switch to Pro).
  const [tuneRefresh, setTuneRefresh] = useState(0);

  useEffect(() => {
    const onUpdate = (e) => {
      if (!e?.detail || e.detail.siloId !== siloId) return;
      setStagingLen(e.detail.length);
    };
    window.addEventListener("staging-updated", onUpdate);
    return () => window.removeEventListener("staging-updated", onUpdate);
  }, [siloId]);

  const recall = (text) => {
    const { length } = appendToStaging(siloId, text);
    setStagingLen(length);
    return length;
  };

  return (
    <div data-testid="operations-tab">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
          <ChartLine size={18} weight="duotone" />
        </div>
        <div>
          <h2 className="font-serif-display text-2xl leading-none">Operations</h2>
          <p className="text-xs text-[#6B6A68] mt-1">
            Cost telemetry for <span className="text-[#1C1B1A]">{siloName}</span>. Click any <em>Output</em> count in the
            report to recall its text into your Workbench staging area — without leaving this tab.
            {stagingLen > 0 && (
              <span className="ml-2 text-[#4A5D4E]">· Staging: <span className="tabular-nums">{stagingLen.toLocaleString()}</span> chars</span>
            )}
          </p>
        </div>
      </div>
      {/* May 25, 2026 — quick-switch LLM header. Shows current Public Q&A
          model + a one-tap Pro↔Flash toggle. Sits above the BYOK/voucher
          cards because the model choice is the single biggest cost lever. */}
      <div className="mb-3">
        <SiloLLMHeaderCard siloId={siloId} siloName={siloName} onChanged={() => setTuneRefresh((n) => n + 1)} />
      </div>

      {/* May 28, 2026 — SiloByokPanel removed. The 3 provider tracker pills
          inside SiloVoucherPanel now cover both jobs (BYOK status + voucher
          balance per provider) so the old single-number Gemini-only BYOK
          tile is redundant. */}
      <div className="mb-4 space-y-3">
        <SiloVoucherPanel siloId={siloId} />
      </div>

      <UsageOverview
        scope="me"
        siloFilter={siloId}
        onRecallToStaging={recall}
      />
      {/* Pro tip — explains the Output-Recirculation-Buffer click target so creators
          discover it without spelunking. Sits directly under Usage Overview. */}
      <div className="mt-4 flex items-start gap-3 bg-[#FDFCF8] border hairline rounded-lg p-3 text-xs text-[#6B6A68]" data-testid="operations-pro-tip">
        <div className="w-7 h-7 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
          <Lightbulb size={14} weight="duotone" />
        </div>
        <div className="leading-relaxed">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E] font-medium mr-1">Pro tip</span>
          In the <span className="text-[#1C1B1A] font-medium">View report</span> above, click on the <span className="text-[#1C1B1A] font-medium">Output</span> number for a quick push of that output to the staging area on the Workbench.
          <span className="ml-1 text-[#6B6A68]">(recalls last 50 queries)</span>
        </div>
      </div>

      {/* Feb 27, 2026 — Per-surface LLM tune. Moved to the BOTTOM of Ops
          (was just under the header) per user request. Advanced power
          users still get to it; new users see voucher/usage first. */}
      <div className="mt-6">
        <SiloSurfaceTunePanel siloId={siloId} refreshSignal={tuneRefresh} />
      </div>
    </div>
  );
}
